// ---------------------------------------------------------------------------
// ExhibitionPage — reusable editorial template for online exhibitions.
// ---------------------------------------------------------------------------
// All visible content comes from the `data` prop, which is the default
// export of a file in src/data/exhibitions/. See
// src/data/exhibitions/timeMachine2025Exhibition.js for the full schema.
//
// Per-show pages (e.g. src/pages/paintings/TimeMachine2025.js) should be
// thin wrappers that just import their exhibition data and render this
// component:
//
//   import ExhibitionPage from "../../components/ExhibitionPage";
//   import exhibition from "../../data/exhibitions/timeMachine2025Exhibition";
//   const TimeMachine2025 = () => <ExhibitionPage data={exhibition} />;
//
// All CSS class names used here live in App.css under the .vr* prefix.
// ---------------------------------------------------------------------------

import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import NavSpacer from "./NavSpacer";
import Breadcrumbs from "./Breadcrumbs";
import BackButton from "./BackButton";
import EnquireButton from "./EnquireButton";

const SITE_ORIGIN = "https://tyronelayne.com";

// Render a small "SOLD" pill in place of the Enquire button.
const SoldLabel = () => (
  <div className='vrSoldLabel' aria-label='This work has been sold'>
    Sold
  </div>
);

// Primary CTA — external Square checkout link. Styled to match
// EnquireButton/PrintLinkButton so all three buttons stack cleanly, and
// replicates the return_url logic used elsewhere on the site (SquareCta,
// BuyPanel) so the Purchase event still fires correctly on the thank-you
// page.
const BuyNowButton = ({ thePrice, theTitle, theLink, label = "Buy Now" }) => {
  const [isHovered, setIsHovered] = useState(false);
  const numericPrice = parseFloat(String(thePrice).replace(/[^0-9.]/g, ""));
  const returnUrl =
    "https://tyronelayne.com/thank-you" +
    "?item=" + encodeURIComponent(theTitle) +
    "&value=" + encodeURIComponent(numericPrice);
  const separator = theLink.includes("?") ? "&" : "?";
  const href = theLink + separator + "return_url=" + encodeURIComponent(returnUrl);

  return (
    <div style={{ textAlign: "center", marginTop: "1.5rem", marginBottom: "0" }}>
      <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          display: "inline-block",
          padding: "14px 32px",
          backgroundColor: isHovered ? "#000" : "transparent",
          color: isHovered ? "#fff" : "#000",
          border: "1px solid #000",
          textDecoration: "none",
          borderRadius: "0",
          fontSize: "0.85rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          transition: "background-color 0.2s ease, color 0.2s ease",
        }}
      >
        {label}
      </a>
      <p style={{ fontSize: "0.78rem", color: "darkgray", margin: "0.6rem 0 0 0" }}>
        Secure payment via Square
      </p>
    </div>
  );
};

// Secondary CTA — internal link to a related print edition, styled to
// match EnquireButton so the two buttons stack cleanly.
const PrintLinkButton = ({ to, label }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div style={{ textAlign: "center", marginTop: "0.75rem", marginBottom: "0" }}>
      <Link
        to={to}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          display: "inline-block",
          padding: "14px 32px",
          backgroundColor: isHovered ? "#000" : "transparent",
          color: isHovered ? "#fff" : "#000",
          border: "1px solid #000",
          textDecoration: "none",
          borderRadius: "0",
          fontSize: "0.85rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          transition: "background-color 0.2s ease, color 0.2s ease",
        }}
      >
        {label}
      </Link>
    </div>
  );
};

function ExhibitionPage({ data }) {
  const {
    hero,
    artistQuote,
    intro,
    paintings,
    closing,
    seo = {},
    runDates = {},
    venue = {},
  } = data;

  // ---------- Parallax background swap ----------
  // A single position:fixed background is anchored to the viewport
  // while the user scrolls through the paintings. (We use fixed
  // rather than sticky because html/.App both have overflow-x:
  // hidden, which forces overflow-y to auto and breaks position:
  // sticky on descendants.)
  //
  // Two IntersectionObservers:
  //   1. On .vrPxScroller — toggles bgVisible so the fixed bg only
  //      shows while the works area is in view.
  //   2. On each .vrPxSection — swaps the bg image as that section
  //      enters the middle band of the viewport.
  //
  // The full-width white cards scroll up over the fixed bg and
  // completely cover it when in view.
  const [activeBg, setActiveBg] = useState(hero.coverImage);
  const [scrollerInView, setScrollerInView] = useState(false);
  const [closingInView, setClosingInView] = useState(false);
  // The fixed bg should only show while the parallax sequence is in
  // view AND the closing/footer card isn't yet visible. The second
  // half of that condition fixes an iOS flicker: when the user scrolls
  // to the closing section, the URL bar collapses and the viewport
  // height jumps. That re-fires the scroller's IntersectionObserver
  // and the bg briefly snaps back on. Force-hiding it as soon as the
  // closing section enters view kills the flicker.
  const bgVisible = scrollerInView && !closingInView;
  const scrollerRef = useRef(null);
  const closingRef = useRef(null);
  const sectionRefs = useRef([]);

  useEffect(() => {
    // Preload the hero / quote / intro backgrounds + every install image
    // so each swap is instant on scroll.
    [hero.coverImage, hero.quoteImage, hero.introImage].forEach((src) => {
      if (!src) return;
      const img = new Image();
      img.src = src;
    });
    paintings.forEach((p) => {
      const img = new Image();
      img.src = p.installImage;
    });
  }, [paintings, hero.coverImage, hero.quoteImage, hero.introImage]);

  // Toggle visibility of the fixed bg based on whether the works
  // wrapper is in view at all. Runs on both desktop and mobile —
  // position:fixed on a dedicated element works reliably across modern
  // mobile browsers (including iOS Safari), so the same parallax
  // behaviour applies everywhere.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const target = scrollerRef.current;
    if (!target) return;

    const obs = new IntersectionObserver(
      ([entry]) => setScrollerInView(entry.isIntersecting),
      { rootMargin: "0px", threshold: 0 }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, []);

  // Watch the closing/footer block so we can pre-emptively hide the
  // fixed bg before the user reaches it. Generous bottom rootMargin
  // means we react as soon as the closing section is approaching the
  // viewport, not after it's already on screen — that's what kills
  // the iOS address-bar flicker.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const target = closingRef.current;
    if (!target) {
      setClosingInView(false);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => setClosingInView(entry.isIntersecting),
      { rootMargin: "0px 0px 200px 0px", threshold: 0 }
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [closing]);

  // Swap the bg image to whichever section is currently in the
  // middle band of the viewport.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry that's most prominently in the middle band.
        let best = null;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        });
        if (best) {
          const next = best.target.dataset.bg;
          if (next) setActiveBg(next);
        }
      },
      {
        // Trigger when the section is in the middle 20% of the viewport.
        rootMargin: "-40% 0px -40% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    sectionRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [paintings]);

  // ---------- SEO ----------
  const canonicalPath = seo.canonicalPath || "/";
  const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`;
  const pageTitle =
    seo.pageTitle ||
    `${hero.title} — Solo Exhibition by Tyrone Layne`;
  const ogTitle =
    seo.ogTitle || `${hero.title} — Solo Exhibition by Tyrone Layne`;
  const metaDescription =
    seo.metaDescription ||
    `${hero.title} — ${hero.subtitle}, ${hero.dates}, ${hero.location}.`;

  // ---------- JSON-LD: ExhibitionEvent + each VisualArtwork ----------
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ExhibitionEvent",
        "name": `${hero.title} — ${hero.subtitle}`,
        ...(runDates.start && { startDate: runDates.start }),
        ...(runDates.end && { endDate: runDates.end }),
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode":
          "https://schema.org/OfflineEventAttendanceMode",
        "location": {
          "@type": "Place",
          "name": venue.name || hero.location || "",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": venue.streetAddress || "",
            "addressLocality": venue.addressLocality || "",
            "addressRegion": venue.addressRegion || "",
            "addressCountry": venue.addressCountry || "",
          },
        },
        "organizer": {
          "@type": "Person",
          "name": "Tyrone Layne",
          "url": SITE_ORIGIN,
        },
        "image": `${SITE_ORIGIN}${
          typeof hero.coverImage === "string" ? hero.coverImage : ""
        }`,
        "url": canonicalUrl,
      },
      ...paintings.map((p) => ({
        "@type": "VisualArtwork",
        "name": p.title,
        "creator": { "@type": "Person", "name": "Tyrone Layne" },
        "dateCreated": p.year,
        "artMedium": p.medium,
        "artworkSurface": p.medium,
        "width": p.dimensions,
        "url": `${canonicalUrl}#${p.slug}`,
      })),
    ],
  };

  return (
    <div>
      <Helmet>
        <title>{pageTitle}</title>
        <link rel='canonical' href={canonicalUrl} />
        <meta name='description' content={metaDescription} />
        <meta property='og:title' content={ogTitle} />
        <meta property='og:description' content={metaDescription} />
        <meta property='og:type' content='article' />
        <meta property='og:url' content={canonicalUrl} />
        {seo.noindex && <meta name='robots' content='noindex, follow' />}
        <script type='application/ld+json'>{JSON.stringify(jsonLd)}</script>
        {/* Preload the hero cover so the fixed background is fetched before JS sets backgroundImage */}
        {hero.coverImage && <link rel='preload' as='image' href={hero.coverImage} fetchpriority='high' />}
      </Helmet>

      <NavSpacer />
      <Breadcrumbs />

      {/* -------- PARALLAX --------
          A single position:fixed background is anchored to the
          viewport for the entire editorial sequence (hero → quote →
          intro → paintings). Each section is a full-width white
          card that scrolls UP over the fixed background. The bg
          image swaps when each section enters the middle band of
          the viewport — driven by the data-bg attribute. On mobile
          the fixed bg is hidden and each painting's install renders
          inline above its card. */}
      <div
        className={`vrPxFixedBg${bgVisible ? " vrPxFixedBg--visible" : ""}`}
        style={{ backgroundImage: `url(${activeBg})` }}
        role='presentation'
        aria-hidden='true'
      />

      <div
        className='vrPxScroller'
        aria-label={`${hero.title} exhibition`}
        ref={scrollerRef}
      >
        {/* ----- HERO section (title / dates / location) ----- */}
        <section
          className='vrPxSection vrPxHeroSection'
          data-bg={hero.coverImage}
          ref={(el) => {
            sectionRefs.current[0] = el;
          }}
        >
          <div className='vrPxGap'>
            <div className='vrPxScrollHint'>
              <span className='vrPxScrollHintText'>
                Scroll down to learn more
              </span>
              <span
                className='vrPxScrollHintArrow'
                aria-hidden='true'
              >
                &#x2193;
              </span>
            </div>
          </div>
          <article className='vrPxFullCard vrPxHeroCard'>
            <div className='vrPxFullCardInner'>
              <p className='vrHeroEyebrow'>{hero.eyebrow || "Solo Exhibition"}</p>
              <h1 className='vrHeroTitle'>{hero.title}</h1>
              <p className='vrHeroSubtitle'>{hero.subtitle}</p>
              <p className='vrHeroDates'>{hero.dates}</p>
              <p className='vrHeroLocation'>{hero.location}</p>
            </div>
          </article>
        </section>

        {/* ----- QUOTE section ----- */}
        {artistQuote && (
          <section
            className='vrPxSection'
            data-bg={hero.quoteImage || hero.coverImage}
            ref={(el) => {
              sectionRefs.current[1] = el;
            }}
          >
            <div className='vrPxGap' aria-hidden='true' />
            <article className='vrPxFullCard'>
              <div className='vrPxFullCardInner'>
                <blockquote className='vrQuoteText'>
                  <em>&ldquo;{artistQuote.text}&rdquo;</em>
                </blockquote>
                <p className='vrQuoteAttribution'>
                  — {artistQuote.attribution}
                </p>
              </div>
            </article>
          </section>
        )}

        {/* ----- INTRO section ----- */}
        {intro && intro.length > 0 && (
          <section
            className='vrPxSection'
            data-bg={hero.introImage || hero.coverImage}
            ref={(el) => {
              sectionRefs.current[2] = el;
            }}
          >
            <div className='vrPxGap' aria-hidden='true' />
            <article className='vrPxFullCard'>
              <div className='vrPxFullCardInner'>
                {intro.map((para, i) => (
                  <p key={i} className='vrIntroPara'>
                    {para}
                  </p>
                ))}
              </div>
            </article>
          </section>
        )}

        {/* ----- PAINTING sections ----- */}
        {paintings.map((p, idx) => (
          <section
            key={p.slug}
            id={p.slug}
            data-bg={p.installImage}
            ref={(el) => {
              // Hero/quote/intro occupy indices 0–2; paintings start at 3.
              sectionRefs.current[idx + 3] = el;
            }}
            className='vrPxSection'
          >
            <div className='vrPxGap' aria-hidden='true' />

            {/* Mobile only: install photo renders inline above the
                card. */}
            <img
              src={p.installImage}
              alt={p.installAlt}
              className='vrPxMobileInstall'
              loading='lazy'
            />

            {/* Full-width white card — completely covers the
                background while it's in view. */}
            <article className='vrPxFullCard'>
              <div className='vrPxFullCardInner'>
                {/* Main painting image — large at top of card. */}
                {p.mainImage && (
                  <figure className='vrWorkMainFigure'>
                    <img
                      src={p.mainImage.src}
                      alt={p.mainImage.alt}
                      className='vrWorkMainImage'
                      loading='lazy'
                    />
                  </figure>
                )}

                <div className='vrWorkCaption'>
                  <p className='vrWorkTitle'>
                    <strong>Tyrone Layne</strong>
                  </p>
                  <p className='vrWorkMeta'>
                    <em>{p.title}</em>, {p.year}
                  </p>
                  <p className='vrWorkMeta'>{p.medium}</p>
                  <p className='vrWorkMeta'>{p.dimensions}</p>
                  {p.price != null && (
                    <p className='vrWorkMeta'>
                      <strong>${p.price.toLocaleString("en-AU")} AUD</strong>
                    </p>
                  )}
                  {p.referenceNote && (
                    <p className='vrWorkReference'>{p.referenceNote}</p>
                  )}
                </div>

                {p.commentary && (
                  <div className='vrWorkCommentary'>
                    {p.commentary.map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                )}

                <div className='vrWorkAction'>
                  {p.status === "sold" ? (
                    <SoldLabel />
                  ) : p.status === "available" ? (
                    <>
                      {p.square && <BuyNowButton {...p.square} />}
                      <EnquireButton
                        showShippingNote={false}
                        paintingTitle={p.title}
                        price={p.price}
                      />
                    </>
                  ) : null}
                  {p.printLink && (
                    <PrintLinkButton
                      to={p.printLink.href}
                      label={p.printLink.label}
                    />
                  )}
                </div>

                {/* Detail + installation images below text/Enquire. */}
                {p.additionalImages && p.additionalImages.length > 0 && (
                  <div
                    className='vrWorkDetailImages'
                    data-count={p.additionalImages.length}
                  >
                    {p.additionalImages.map((img, i) => (
                      <figure key={i} className='vrWorkDetailFigure'>
                        <img
                          src={img.srcSm || img.src}
                          alt={img.alt}
                          className='vrWorkDetailImage'
                          loading='lazy'
                        />
                        {img.caption && (
                          <figcaption className='vrWorkDetailCaption'>
                            {img.caption}
                          </figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                )}
              </div>
            </article>
          </section>
        ))}
      </div>

      {/* -------- CLOSING / INSTALLATION -------- */}
      {closing && (
        <section className='vrClosing' ref={closingRef}>
          <figure className='vrClosingFigure'>
            <img
              src={closing.image}
              alt={closing.alt}
              className='vrClosingImage'
              loading='lazy'
            />
            {closing.caption && (
              <figcaption className='vrClosingCaption'>
                {closing.caption}
              </figcaption>
            )}
          </figure>
          {closing.body && <p className='vrClosingBody'>{closing.body}</p>}
        </section>
      )}

      <BackButton />
    </div>
  );
}

export default ExhibitionPage;
